/**
 * Print an on-screen report matrix (headers + rows).
 *
 * Why an iframe and not window.open: the previous implementation called
 * window.open('', '_blank', 'noopener,noreferrer'). Per the HTML spec a
 * window opened with `noopener` returns NULL to the opener — always, in every
 * browser, with no pop-up blocker involved. The null was then reported to the
 * user as "Pop-up blocked", which was never true. Printing from a hidden
 * same-document iframe removes the pop-up dependency entirely: nothing to
 * block, nothing to allow, and it works with the browser's default settings.
 */

export type PrintGrid = {
  headers: string[]
  rows: string[][]
  title?: string
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export function buildPrintHtml(grid: PrintGrid): string {
  const title = grid.title?.trim() || 'Pipeline Report'
  const stamp = new Date().toLocaleString()
  const headCells = grid.headers.map((h) => `<th>${escapeHtml(h)}</th>`).join('')
  const bodyRows = grid.rows
    .map(
      (row, i) =>
        `<tr class="${i % 2 ? 'alt' : ''}">${row
          .map((cell) => `<td>${escapeHtml(cell)}</td>`)
          .join('')}</tr>`,
    )
    .join('')

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>
    @page { size: landscape; margin: 0.4in; }
    body { font-family: Helvetica, Arial, sans-serif; color: #333; margin: 0; padding: 12px 16px; }
    h1 { font-size: 18px; color: #1A2D5D; margin: 0 0 4px; font-weight: 700; }
    .meta { font-size: 11px; color: #6B6459; margin: 0 0 14px; }
    table { border-collapse: collapse; width: 100%; table-layout: fixed; }
    th, td {
      border: 0.5pt solid #A8BDD4; padding: 4px 5px; font-size: 8px; text-align: center;
      vertical-align: middle; word-wrap: break-word;
    }
    th { background: #D6E3F0; color: #000; font-weight: 700; }
    tr.alt td { background: #EAF1F8; }
  </style>
</head>
<body>
  <h1>${escapeHtml(title)}</h1>
  <p class="meta">${grid.rows.length} deal${grid.rows.length === 1 ? '' : 's'} &middot; on-screen columns &middot; ${escapeHtml(stamp)}</p>
  <table>
    <thead><tr>${headCells}</tr></thead>
    <tbody>${bodyRows}</tbody>
  </table>
</body>
</html>`
}

/**
 * Render `html` in an offscreen iframe and open the browser print dialog for
 * it. The iframe is removed once the print dialog closes (afterprint), with a
 * timed sweep as the backstop for browsers that never fire it.
 */
export function printHtmlDocument(html: string): void {
  const frame = document.createElement('iframe')
  // Offscreen rather than display:none — a display:none frame does not lay out
  // in some engines, which yields a blank print.
  frame.setAttribute('aria-hidden', 'true')
  frame.style.position = 'fixed'
  frame.style.right = '0'
  frame.style.bottom = '0'
  frame.style.width = '1px'
  frame.style.height = '1px'
  frame.style.border = '0'
  frame.style.opacity = '0'
  frame.style.pointerEvents = 'none'

  let cleaned = false
  const cleanup = () => {
    if (cleaned) return
    cleaned = true
    window.setTimeout(() => frame.remove(), 0)
  }

  frame.onload = () => {
    const win = frame.contentWindow
    if (!win) {
      cleanup()
      return
    }
    try {
      win.addEventListener('afterprint', cleanup, { once: true })
    } catch {
      /* afterprint unsupported — the sweep below removes the frame */
    }
    // A frame paints before it is printable in Safari/WebKit; one tick is enough.
    window.setTimeout(() => {
      try {
        win.focus()
        win.print()
      } catch {
        /* the document is rendered; the user can print with Ctrl+P */
      }
      window.setTimeout(cleanup, 60_000)
    }, 50)
  }

  document.body.appendChild(frame)
  // srcdoc keeps the frame same-origin without a blob URL to revoke.
  frame.srcdoc = html
}

export function printViewGrid(grid: PrintGrid): void {
  printHtmlDocument(buildPrintHtml(grid))
}
